import { useCallback, useRef } from "react";
import type { PointerEvent as ReactPointerEvent } from "react";

const LONG_PRESS_MS = 500;
/** Deslocamento a partir do qual entendemos que o usuário está rolando a lista, não pressionando. */
const MOVE_TOLERANCE_PX = 10;

interface UseLongPressOptions {
  onLongPress: () => void;
  disabled?: boolean;
}

/**
 * Long-press para abrir ações contextuais no mobile (equivalente ao hover +
 * menu do desktop). Cancela ao rolar e suprime o clique que o navegador
 * dispararia logo depois.
 */
export function useLongPress({ onLongPress, disabled }: UseLongPressOptions) {
  const timerRef = useRef<number | null>(null);
  const originRef = useRef<{ x: number; y: number } | null>(null);
  const firedRef = useRef(false);

  const clear = useCallback(() => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    originRef.current = null;
  }, []);

  const onPointerDown = useCallback(
    (event: ReactPointerEvent) => {
      if (disabled || event.button === 2) return;

      firedRef.current = false;
      originRef.current = { x: event.clientX, y: event.clientY };
      timerRef.current = window.setTimeout(() => {
        firedRef.current = true;
        timerRef.current = null;
        onLongPress();
      }, LONG_PRESS_MS);
    },
    [disabled, onLongPress]
  );

  const onPointerMove = useCallback(
    (event: ReactPointerEvent) => {
      const origin = originRef.current;
      if (!origin || timerRef.current === null) return;

      const moved =
        Math.abs(event.clientX - origin.x) > MOVE_TOLERANCE_PX ||
        Math.abs(event.clientY - origin.y) > MOVE_TOLERANCE_PX;
      if (moved) clear();
    },
    [clear]
  );

  const onClickCapture = useCallback((event: React.MouseEvent) => {
    // Evita que o long-press também acione o conteúdo da bolha (mídia, links).
    if (firedRef.current) {
      firedRef.current = false;
      event.preventDefault();
      event.stopPropagation();
    }
  }, []);

  return {
    onPointerDown,
    onPointerMove,
    onPointerUp: clear,
    onPointerCancel: clear,
    onPointerLeave: clear,
    onClickCapture,
    /** Impede o menu nativo de seleção no toque longo do Android/iOS. */
    onContextMenu: (event: ReactPointerEvent | React.MouseEvent) => event.preventDefault(),
  };
}
